import { assign, createMachine } from 'xstate';
import { trackClickEvent, trackViewEvent } from 'domain/tracking';
import {
  createPaymentHold,
  createSetupIntent,
  pollForPaymentCreation,
  setPaymentMethodOnPlans,
} from './services';
import * as Screens from './screens';

export const addPaymentMethodMachine = createMachine(
  {
    id: 'add-payment-flow',
    context: {
      activePlans: undefined,
      isActivePlanWhale: undefined,
      accountId: undefined,
      existingPaymentMethodIds: undefined,
      stripeClientSecret: undefined,
      mutationVariablesForSetPayment: undefined,
      newPaymentMethod: undefined,
      updatedPlans: undefined,
      errors: {},
    } as AddPaymentMethodContext,
    initial: 'addPaymentMethod',
    states: {
      // ***** ADD PAYMENT METHOD SCREEN *****
      addPaymentMethod: {
        id: 'addPaymentMethod',
        initial: 'createSetupIntent',
        meta: { Component: Screens.AddPaymentMethod },
        entry: 'trackAddingScreen',
        states: {
          createSetupIntent: {
            id: 'createSetupIntent',
            invoke: {
              src: 'createSetupIntent',
              onDone: {
                actions: 'setStripeClientSecret',
                target: 'idle',
              },
              onError: {
                actions: 'setCreateSetupIntentError',
                target: '#error',
              },
            },
          },
          idle: {
            on: {
              next: {
                actions: 'trackAddAndContinue',
                target: 'pollForPaymentCreation',
              },
            },
          },
          pollForPaymentCreation: {
            id: 'pollForPaymentCreation',
            invoke: {
              src: 'pollForPaymentCreation',
              onDone: [
                {
                  cond: 'skipToConfirmation',
                  actions: [
                    'setNewPaymentMethod',
                    'updateMutationVariablesToSetOnAllPlans',
                  ],
                  target: 'setPaymentMethodOnPlans',
                },
                {
                  actions: 'setNewPaymentMethod',
                  target: '#setPaymentMethod',
                },
              ],
              onError: {
                actions: 'setPollingError',
                target: '#error',
              },
            },
          },
          setPaymentMethodOnPlans: {
            id: 'setPaymentMethodOnPlans',
            invoke: {
              src: 'setPaymentMethodOnPlans',
              onDone: {
                actions: 'setUpdatedPlans',
                target: '#confirmation',
              },
            },
          },
        },
      },
      // ***** SET PAYMENT METHOD SCREEN *****
      setPaymentMethod: {
        id: 'setPaymentMethod',
        initial: 'idle',
        meta: { Component: Screens.SetPaymentMethod },
        entry: 'trackSettingScreen',
        states: {
          idle: {
            on: {
              toggleChange: { actions: 'updateMutationVariablesForSetPayment' },
              next: [
                {
                  cond: 'declinedToSetPayment',
                  actions: 'trackSaveChanges',
                  target: '#confirmation',
                },
                {
                  actions: 'trackSaveChanges',
                  target: 'setPaymentMethodOnPlans',
                },
              ],
            },
          },
          setPaymentMethodOnPlans: {
            id: 'setPaymentMethodOnPlans',
            invoke: {
              src: 'setPaymentMethodOnPlans',
              onDone: {
                actions: 'setUpdatedPlans',
                target: '#confirmation',
              },
            },
          },
        },
      },
      // ***** CONFIRMATION SCREEN *****
      confirmation: {
        id: 'confirmation',
        meta: { Component: Screens.Confirmation },
        entry: 'trackConfirmationScreen',
        type: 'final',
      },
      // ***** ERROR SCREEN *****
      error: {
        id: 'error',
        meta: { Component: Screens.Error },
        type: 'final',
      },
    },
  },
  {
    actions: {
      setStripeClientSecret: assign((_context, event: PaymentEvent) => ({
        stripeClientSecret: event.data.stripeClientSecret,
      })),
      updateMutationVariablesToSetOnAllPlans: assign(context => ({
        mutationVariablesForSetPayment: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          accountId: context.accountId!,
          input: {
            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            paymentMethodId: context.newPaymentMethod?.id!,
            planIds: null,
          },
        },
      })),
      updateMutationVariablesForSetPayment: assign(
        (_context, event: PaymentEvent) => ({
          mutationVariablesForSetPayment:
            event.data.mutationVariablesForSetPayment,
        })
      ),
      setNewPaymentMethod: assign((_context, event: PaymentEvent) => ({
        newPaymentMethod: event.data.newPaymentMethod,
      })),
      setUpdatedPlans: assign((_context, event: PaymentEvent) => ({
        updatedPlans: event.data.updatedPlans,
      })),
      setCreateSetupIntentError: assign((context, event: PaymentEvent) => ({
        errors: { ...context.errors, createSetupIntent: event.data.message },
      })),
      setPollingError: assign((context, event: PaymentEvent) => {
        const error = event.data?.message;
        if (error === 'Payment hold failed') {
          return {
            errors: { ...context.errors, paymentHold: error },
          };
        } else {
          return {
            errors: { ...context.errors, pollForPaymentCreation: error },
          };
        }
      }),
      setSetPaymentError: assign((context, event: PaymentEvent) => ({
        errors: { ...context.errors, setPaymentMethod: event.data.message },
      })),
      trackAddAndContinue: () =>
        trackClickEvent('paymentInformation.addPaymentModal.addAndContinue'),
      trackSaveChanges: () =>
        trackClickEvent('paymentInformation.addPaymentModal.saveChanges'),
      trackAddingScreen: () =>
        trackViewEvent('paymentInformation.addPaymentModal.addingScreen'),
      trackSettingScreen: () =>
        trackViewEvent('paymentInformation.addPaymentModal.settingScreen'),
      trackConfirmationScreen: () =>
        trackViewEvent('paymentInformation.addPaymentModal.confirmationScreen'),
    },
    guards: {
      skipToConfirmation: (context: AddPaymentMethodContext) =>
        context.activePlans?.length === 1,
      declinedToSetPayment: (context: AddPaymentMethodContext) =>
        !context.mutationVariablesForSetPayment,
    },
    services: {
      createSetupIntent: async context => {
        const result = await createSetupIntent(context);
        return result;
      },
      pollForPaymentCreation: async context => {
        const pollResult = await pollForPaymentCreation(context);
        if (
          pollResult.newPaymentMethod.type === 'credit_card' &&
          context.shouldCreatePaymentHold
        ) {
          const holdResult = await createPaymentHold({
            accountId: context.accountId!,
            deletePaymentMethodOnFailure: true,
            paymentMethodId: pollResult.newPaymentMethod.id,
          });
          if (holdResult.result !== 'success') {
            throw new Error('Payment hold failed');
          }
        }
        return pollResult;
      },
      setPaymentMethodOnPlans: async context => {
        const result = await setPaymentMethodOnPlans(context);
        return result;
      },
    },
  }
);
